import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { fetchPatientById, type Patient } from '@/services/patient-service';
import { toast } from 'sonner';
import { formatCPF, formatPhone, formatDateBR } from "@/utils/formatters";
import { PatientAppointmentsTab } from '@/components/patients/PatientAppointmentsTab';
import { FileText } from 'lucide-react';

export default function MedicalRecordDetail() {
  const { patientId } = useParams<{ patientId: string }>();
  const navigate = useNavigate();
  const [patient, setPatient] = useState<Patient | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadPatient();
  }, [patientId]);

  const loadPatient = async () => {
    if (!patientId) return;
    try {
      setLoading(true);
      const data = await fetchPatientById(patientId, true);
      if (data && data.id === patientId) {
        setPatient(data);
      } else {
        toast.error('Paciente não encontrado no banco de dados');
        navigate('/patients');
      }
    } catch (error) {
      toast.error('Erro ao carregar dados do paciente');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <p className="text-muted-foreground animate-pulse text-xs font-bold uppercase tracking-widest">Carregando prontuário...</p>
      </div>
    );
  }

  if (!patient) return null;

  const UnderConstruction = () => (
    <div className="flex flex-col items-center justify-center py-12 text-slate-400 border-2 border-dashed rounded-lg bg-slate-50/50">
      <p className="text-xs font-medium uppercase tracking-widest">Em construção</p>
    </div>
  );

  return (
    <div className="page-wrapper">
      <div>
        <div className="flex items-center gap-2">
          <FileText className="page-icon" />
          <h1 className="page-title">Prontuário Eletrônico</h1>
        </div>
        <p className="page-subtitle">
          Histórico clínico de <span className="font-semibold uppercase">{patient.name}</span>
        </p>
      </div>

      <Card className="border-border shadow-sm">
        <CardHeader className="py-2 px-3 bg-muted/50 border-b">
          <CardTitle className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
            Dados de Identificação
          </CardTitle>
        </CardHeader>
        <CardContent className="px-3 py-2">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-2">
            <div className="flex flex-col">
              <span className="text-muted-foreground text-[10px] font-bold mb-0.5 uppercase">Nome</span>
              <span className="text-xs font-medium uppercase">{patient.name}</span>
            </div>
            <div className="flex flex-col">
              <span className="text-muted-foreground text-[10px] font-bold mb-0.5 uppercase">CPF</span>
              <span className="text-xs font-medium">{formatCPF(patient.cpf)}</span>
            </div>
            <div className="flex flex-col">
              <span className="text-muted-foreground text-[10px] font-bold mb-0.5 uppercase">Telefone</span>
              <span className="text-xs font-medium">{formatPhone(patient.cellphone)}</span>
            </div>
            <div className="flex flex-col">
              <span className="text-muted-foreground text-[10px] font-bold mb-0.5 uppercase">Nascimento</span>
              <span className="text-xs font-medium">{formatDateBR(patient.birth_date)}</span>
            </div>
          </div>
        </CardContent>
      </Card>

      <Tabs defaultValue="appointments" className="w-full">
        <TabsList className="grid w-full grid-cols-7 bg-muted p-0.5 rounded-lg h-8">
          <TabsTrigger value="appointments" className="text-[10px] font-bold uppercase h-7">Atendimentos</TabsTrigger>
          <TabsTrigger value="comunicados" className="text-[10px] font-bold uppercase h-7">Comunicados</TabsTrigger>
          <TabsTrigger value="financeiro" className="text-[10px] font-bold uppercase h-7">Financeiro</TabsTrigger>
          <TabsTrigger value="anamnesis" className="text-[10px] font-bold uppercase h-7">Anamnese</TabsTrigger>
          <TabsTrigger value="treatments" className="text-[10px] font-bold uppercase h-7">Tratamentos</TabsTrigger>
          <TabsTrigger value="attachments" className="text-[10px] font-bold uppercase h-7">Anexos</TabsTrigger>
          <TabsTrigger value="odontogram" className="text-[10px] font-bold uppercase h-7">Odontograma</TabsTrigger>
        </TabsList>

        <div className="mt-2 bg-card border border-border rounded-lg p-3 shadow-sm min-h-[300px]">
          <TabsContent value="appointments" className="mt-0">
            <PatientAppointmentsTab patientId={patientId!} />
          </TabsContent>
          <TabsContent value="comunicados" className="mt-0"><UnderConstruction /></TabsContent>
          <TabsContent value="financeiro" className="mt-0"><UnderConstruction /></TabsContent>
          <TabsContent value="anamnesis" className="mt-0"><UnderConstruction /></TabsContent>
          <TabsContent value="treatments" className="mt-0"><UnderConstruction /></TabsContent>
          <TabsContent value="attachments" className="mt-0"><UnderConstruction /></TabsContent>
          <TabsContent value="odontogram" className="mt-0"><UnderConstruction /></TabsContent>
        </div>
      </Tabs>
    </div>
  );
}
